<template>
    <div>
        <FormattingControls class="mt-3" :editor="editor">
            <!-- Use the after-controls slot for the templates dropdown -->
            <template #after-controls>
                <!-- Templates dropdown menu -->
                <v-menu
                    offset-y
                    open-on-hover
                    :open-delay="100"
                    :close-delay="300"
                    :close-on-content-click="true"
                    transition="slide-y-transition"
                >
                    <template v-slot:activator="{ on, attrs }">
                        <v-btn
                            text
                            outlined
                            dense
                            class="mb-sm-3 mb-1 templates-btn"
                            v-bind="attrs"
                            v-on="on"
                            :small="$vuetify.breakpoint.xsOnly"
                        >
                            <v-icon left :small="$vuetify.breakpoint.xsOnly" class="mr-2">mdi-file-document-multiple-outline</v-icon>
                            <span class="text-button">Vorlagen</span>
                            <v-icon small class="ml-1">mdi-chevron-down</v-icon>
                        </v-btn>
                    </template>
                    
                    <v-card>
                        <v-card-title class="py-2 px-4">
                            <div class="d-flex align-center">
                                <span class="text-subtitle-2 font-weight-medium">Vorlagen</span>
                                <v-tooltip bottom open-delay="300">
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-icon small class="ml-2" color="grey darken-1" v-bind="attrs" v-on="on">
                                            mdi-information-outline
                                        </v-icon>
                                    </template>
                                    <span>
                                        Hier kannst du Vorlagen erstellen und verwalten.<br>
                                        Vorlagen eignen sich besonders für wiederkehrende Inhalte wie z.B. Anamnesebögen, Therapiebeginn-Checklisten und ähnliches.<br>
                                        Klicke auf eine Vorlage, um sie zu laden.
                                        Du kannst wählen, ob du den Inhalt ersetzen oder anfügen möchtest.
                                    </span>
                                </v-tooltip>
                            </div>
                        </v-card-title>
                        
                        <v-divider></v-divider>
                        
                        <div v-if="fetching_templates" class="templates-loading pa-4">
                            <v-progress-circular color="primary" size="20" class="mr-2" indeterminate />
                            <span class="text-body-2">Lade Vorlagen...</span>
                        </div>
                        
                        <v-list dense v-else style="max-height: 300px; overflow-y: auto;">
                            <!-- Create new template list item -->
                            <v-list-item @click="openNewTemplateDialog" class="new-template-item">
                                <v-list-item-icon>
                                    <v-icon :color="$store.state.theme.green">mdi-plus</v-icon>
                                </v-list-item-icon>
                                <v-list-item-content>
                                    <v-list-item-title>Neue Vorlage erstellen</v-list-item-title>
                                </v-list-item-content>
                            </v-list-item>
                            
                            <v-divider v-if="templates.length > 0"></v-divider>
                            
                            <!-- Template list items -->
                            <v-list-item 
                                v-for="(item, i) in templates" 
                                :key="`template-${i}`"
                                @click="onTemplateSelected(item)"
                                class="template-item"
                            >
                                <v-list-item-icon>
                                    <v-icon>mdi-file-document-outline</v-icon>
                                </v-list-item-icon>
                                <v-list-item-content>
                                    <v-list-item-title>{{ item.name }}</v-list-item-title>
                                </v-list-item-content>
                                <v-list-item-action class="d-flex flex-row">
                                    <v-tooltip bottom open-delay="300">
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-icon
                                                small
                                                class="action-icon mr-2"
                                                v-bind="attrs"
                                                v-on="on"
                                                @click.stop="openEditTemplateDialog(item)"
                                                :disabled="item.deleting"
                                            >
                                                mdi-pencil
                                            </v-icon>
                                        </template>
                                        <span>Vorlage bearbeiten</span>
                                    </v-tooltip>
                                    
                                    <v-tooltip bottom open-delay="300">
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-icon
                                                small
                                                class="action-icon"
                                                v-bind="attrs"
                                                v-on="on"
                                                @click.stop="deleteTemplate(item)"
                                                :disabled="item.deleting"
                                                :color="$store.state.theme.red"
                                            >
                                                mdi-delete
                                            </v-icon>
                                        </template>
                                        <span>Vorlage löschen</span>
                                    </v-tooltip>
                                </v-list-item-action>
                            </v-list-item>
                        </v-list>
                    </v-card>
                </v-menu>
            </template>
        </FormattingControls>

        <editor-content class="mt-0" :editor="editor"></editor-content>

        <v-dialog v-model="showNewTemplateDialog" max-width="1000px" persistent
            :fullscreen="$vuetify.breakpoint.xsOnly || isFullscreen">
            <v-card>
                <v-card-title class="mr-4 pr-0">
                    <span class="text-h5">{{ isEditingTemplate ? 'Vorlage bearbeiten' : 'Neue Vorlage erstellen'
                        }}</span>
                    <v-spacer></v-spacer>
                    <v-tooltip bottom open-delay="300" v-if="$vuetify.breakpoint.smAndUp">
                        <template v-slot:activator="{ on, attrs }">
                            <v-btn icon @click="toggleFullscreen" v-bind="attrs" v-on="on">
                                <v-icon>{{ isFullscreen ? 'mdi-arrow-collapse' : 'mdi-arrow-expand' }}</v-icon>
                            </v-btn>
                        </template>
                        {{ isFullscreen ? 'Vollbild beenden.' : 'In den Vollbildmodus wechseln.' }}
                    </v-tooltip>
                </v-card-title>
                <v-card-text>
                    <v-text-field dense outlined label="Name der Vorlage" v-model="template_name" class="mt-3 mb-0"
                        :error-messages="templateNameErrors" @input="validateTemplateName" ref="templateNameInput" />
                    <div class="mt-0 mb-0">
                        <FormattingControls :editor="newTemplateEditor" />
                    </div>
                    <editor-content class="mt-0" :editor="newTemplateEditor"></editor-content>
                </v-card-text>
                <v-card-actions class="pb-5 px-6">
                    <v-spacer />
                    <v-btn :color="$store.state.theme.primary" text @click="closeNewTemplateDialog"
                        :disabled="saving_template">Abbrechen</v-btn>
                    <v-btn :color="$store.state.theme.green" outlined @click="saveNewTemplate"
                        :disabled="!isTemplateNameValid" :loading="saving_template">
                        Speichern
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog v-model="showDialog" width="600">
            <v-card>
                <div class="d-flex align-center px-4 pt-4">
                    <v-card-title class="text-h5 pa-0">Vorlage laden</v-card-title>
                    <v-spacer></v-spacer>
                    <v-btn icon @click="cancelTemplateLoad">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </div>
                <v-card-text class="text-body-1 pt-4 pb-2">
                    <p>Wie möchtest du die Vorlage <strong>"{{ templateToLoad ? templateToLoad.name : '' }}"</strong> verwenden?</p>
                    <p class="text-body-2 mt-3"><strong>Ersetzen:</strong> Der aktuelle Inhalt wird vollständig durch die Vorlage ersetzt.</p>
                    <p class="text-body-2"><strong>Anfügen:</strong> Die Vorlage wird am Ende des aktuellen Inhalts hinzugefügt.</p>
                </v-card-text>
                <v-card-actions class="pt-0 pb-6 px-6">
                    <v-spacer></v-spacer>
                    <v-btn 
                        :color="$store.state.theme.red"
                        outlined
                        class="mr-4"
                        @click="loadTemplateWithMode('replace')"
                    >
                        <v-icon left>mdi-file-replace-outline</v-icon>
                        Ersetzen
                    </v-btn>
                    <v-btn 
                        :color="$store.state.theme.green"
                        @click="loadTemplateWithMode('append')"
                        outlined
                    >
                        <v-icon left>mdi-file-plus-outline</v-icon>
                        Anfügen
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Delete confirmation dialog -->
        <v-dialog v-model="showDeleteDialog" width="600" persistent>
            <v-card>
                <v-card-title class="text-h5">Vorlage löschen</v-card-title>
                <v-card-text class="text-body-1 mt-5">
                    <p>Bist du sicher, dass du die Vorlage <strong>"{{ templateToDelete ? templateToDelete.name : '' }}"</strong> löschen möchtest?</p>
                    <p>Diese Aktion kann nicht rückgängig gemacht werden.</p>
                </v-card-text>
                <v-card-actions class="mx-3 pb-5">
                    <v-spacer />
                    <v-btn :color="$store.state.theme.primary" text @click="showDeleteDialog = false">Abbrechen</v-btn>
                    <v-btn :color="$store.state.theme.red" outlined @click="confirmDeleteTemplate">Löschen</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import { Editor, EditorContent } from '@tiptap/vue-2'
import StarterKit from '@tiptap/starter-kit'
import Underline from '@tiptap/extension-underline'
import Highlight from '@tiptap/extension-highlight'
import TextAlign from '@tiptap/extension-text-align'
import TaskList from '@tiptap/extension-task-list'
import TaskItem from '@tiptap/extension-task-item'
import Table from '@tiptap/extension-table'
import TableRow from '@tiptap/extension-table-row'
import TableCell from '@tiptap/extension-table-cell'
import TableHeader from '@tiptap/extension-table-header'
import FormattingControls from '../components/FormattingControls.vue'
import connector from '../helpers/supabase-connector.js'

export default {

    components: {
        EditorContent,
        FormattingControls
    },

    emits: ['input', 'showError', 'showInfo'],

    props: {
        value: {
            type: String,
            default: '',
        },

        session: {
            type: Object,
            default: null
        },
    },

    data() {
        return {
            fetching_templates: false,
            templates: [],
            template_name: '',
            editor: null,
            showDialog: false, // Flag for warning dialog
            templateToLoad: null, // Store the template to load after confirmation

            showNewTemplateDialog: false,
            newTemplateEditor: null,

            templateNameErrors: [],

            saving_template: false,
            isFullscreen: false,

            isEditingTemplate: false,
            editingTemplateId: null,
            
            showDeleteDialog: false,
            templateToDelete: null,
        };
    },

    watch: {
        value(value) {
            // HTML
            const isSame = this.editor.getHTML() === value;

            if (isSame) {
                return;
            }
            this.editor.commands.setContent(value, false);
        },
    },

    async mounted() {
        this.editor = new Editor({
            editorProps: {
                attributes: {
                    class: 'my-2 mx-auto focus:outline-none',
                },
            },
            extensions: [
                StarterKit.configure({
                    heading: {
                        levels: [1, 2, 3],
                    }
                }),
                Underline,
                Highlight.configure({
                    multicolor: true,
                }),
                TextAlign.configure({
                    types: ['heading', 'paragraph'],
                }),
                TaskList,
                TaskItem.configure({
                    nested: true,
                }),
                Table.configure({
                    resizable: true,
                }),
                TableRow,
                TableHeader,
                TableCell,
            ],
            content: this.value,
            onUpdate: () => {
                // HTML
                this.$emit('input', this.editor.getHTML())
            },
        });

        this.newTemplateEditor = new Editor({
            editorProps: {
                attributes: {
                    class: 'my-2 mx-auto focus:outline-none',
                },
            },
            extensions: [
                StarterKit.configure({
                    heading: {
                        levels: [1, 2, 3],
                    }
                }),
                Underline,
                Highlight.configure({
                    multicolor: true,
                }),
                TextAlign.configure({
                    types: ['heading', 'paragraph'],
                }),
                TaskList,
                TaskItem.configure({
                    nested: true,
                }),
                Table.configure({
                    resizable: true,
                }),
                TableRow,
                TableHeader,
                TableCell,
            ],
            content: '',
        });

        await this.fetchTemplates();
    },

    computed: {
        isTemplateNameValid() {
            return this.templateNameErrors.length === 0;
        },
    },

    methods: {

        async fetchTemplates() {
            this.fetching_templates = true;
            let templates = await connector.getDataOnly(this, 'vwdokumentationsvorlagen', 'name', true);
            if (templates === -1) {
                // error has already been displayed
                this.templates = [];
            } else {
                this.templates = templates.map((template) => {
                    template.deleting = false;
                    return template;
                });
            }
            this.fetching_templates = false;
        },

        toggleFullscreen() {
            this.isFullscreen = !this.isFullscreen;
        },

        validateTemplateName() {
            this.templateNameErrors = []; // Clear previous errors

            if (this.template_name.length === 0) {
                this.templateNameErrors.push("Bitte gib einen Namen für die Vorlage ein.");
            }

            if (this.template_name.length > 40) {
                this.templateNameErrors.push("Der Name der Vorlage darf maximal 40 Zeichen lang sein.");
            }

            // Exclude the current template being edited from duplicate check
            const otherTemplates = this.templates.filter(t => t.id !== this.editingTemplateId);
            if (otherTemplates.some(t => t.name === this.template_name)) {
                this.templateNameErrors.push("Eine Vorlage mit diesem Namen existiert bereits.");
            }
        },

        closeNewTemplateDialog() {
            this.showNewTemplateDialog = false;
            this.$nextTick(() => {
                this.clearHistoryTemplate();
                this.isEditingTemplate = false;
                this.editingTemplateId = null;
            });
        },

        openNewTemplateDialog() {
            this.showNewTemplateDialog = true;

            this.isEditingTemplate = false;

            // Use $nextTick inside a $nextTick to ensure the dialog is rendered
            this.$nextTick(() => {
                this.$nextTick(() => {
                    this.validateTemplateName();

                    // Now focus on the input
                    if (this.$refs.templateNameInput) {
                        this.$refs.templateNameInput.focus();
                    }
                });
            });
        },

        openEditTemplateDialog(template) {
            this.isEditingTemplate = true;
            this.editingTemplateId = template.id;
            this.template_name = template.name;
            this.newTemplateEditor.commands.setContent(template.content);
            this.showNewTemplateDialog = true;

            this.$nextTick(() => {
                this.$nextTick(() => {
                    this.validateTemplateName();
                    if (this.$refs.templateNameInput) {
                        this.$refs.templateNameInput.focus();
                    }
                });
            });
        },

        async saveNewTemplate() {
            this.saving_template = true;
            const content = this.newTemplateEditor.getHTML();

            try {
                if (this.isEditingTemplate) {
                    let updated = await connector.update(this, 'dokumentationsvorlagen', {
                        name: this.template_name,
                        content: content
                    }, this.editingTemplateId);
                    if (updated === null) {
                        // error has already been shown
                        this.saving_template = false;
                        return;
                    }
                } else {
                    let inserted = await connector.insert(this, 'dokumentationsvorlagen', {
                        uid: this.session.user.id,
                        name: this.template_name,
                        content: content
                    });
                    if (inserted === false) {
                        // error has already been shown
                        this.saving_template = false;
                        return;
                    }
                }

                await this.fetchTemplates();
                this.clearHistoryTemplate();
                this.showNewTemplateDialog = false;
                this.$emit('showInfo', {
                    message: this.isEditingTemplate ? 'Die Vorlage wurde erfolgreich aktualisiert.' : 'Die Vorlage wurde erfolgreich erstellt.',
                    timeout: 5000
                });
                this.isEditingTemplate = false;
            } catch (error) {
                // Handle the error, e.g., show an error message to the user.
                console.error('Error saving new template:', error);
            } finally {
                this.saving_template = false;
            }
        },

        async deleteTemplate(template) {
            this.templateToDelete = template;
            this.showDeleteDialog = true;
        },

        async confirmDeleteTemplate() {
            this.showDeleteDialog = false;
            
            if (!this.templateToDelete) return;
            
            const template = this.templateToDelete;
            let index = this.templates.findIndex((t) => t.id === template.id);
            
            if (index > -1) {
                this.$set(this.templates[index], 'deleting', true);
                let deleted = await connector.delete(this, 'dokumentationsvorlagen', 'id', template.id);
                await this.fetchTemplates();
                if (deleted) {
                    this.$emit('showInfo', {
                        message: 'Die Vorlage wurde erfolgreich gelöscht.',
                        timeout: 5000
                    });
                } // in case of error, a message was already shown
            } else {
                this.$emit('showError', {
                    message: 'Die Vorlage wurde nicht gefunden.',
                    timeout: 5000
                });
            }
            
            // Reset the template to delete
            this.templateToDelete = null;
        },

        onTemplateSelected(templateId) {
            if (!(this.editor.getHTML() === '' || this.editor.getHTML() === '<p></p>')) {
                // Show options dialog
                this.templateToLoad = templateId;
                this.showDialog = true;
            } else {
                // Load template directly (replace mode is fine for empty content)
                this.loadTemplate(templateId, 'replace');
            }
        },

        loadTemplateWithMode(mode) {
            this.loadTemplate(this.templateToLoad, mode);
        },

        loadTemplate(templateId, mode) {
            const template = this.templates.find(t => t.id === templateId.id);
            if (template) {
                if (mode === 'replace') {
                    this.setContent(template.content, true);
                } else if (mode === 'append') {
                    // First position cursor at end of document
                    this.editor.commands.focus('end');
                    
                    // If editor isn't empty, insert a paragraph break first
                    const isEditorEmpty = this.editor.getHTML() === '' || this.editor.getHTML() === '<p></p>';
                    if (!isEditorEmpty) {
                        this.editor.commands.insertContent('<p></p>');
                    }
                    
                    // Then insert the template content
                    this.editor.commands.insertContent(template.content);
                }
                this.showDialog = false;
            }
        },
        
        cancelTemplateLoad() {
            this.showDialog = false;
            this.templateToLoad = null;
        },

        // this method is called from the parent
        clearHistory() {
            this.editor.commands.clearContent(true);
            this.editor.state.history$.prevRanges = null;
            this.editor.state.history$.done.eventCount = 0;

            // reset templates
            this.templateToLoad = null;
        },

        clearHistoryTemplate() {
            this.newTemplateEditor.commands.clearContent(true);
            this.newTemplateEditor.state.history$.prevRanges = null;
            this.newTemplateEditor.state.history$.done.eventCount = 0;
            this.template_name = '';
        },

        setContent(content, update=false) {
            this.editor.commands.setContent(content, update);
        },
    },

    beforeDestroy() {
        if (this.editor) {
            this.editor.destroy();
        }
        if (this.newTemplateEditor) {
            this.newTemplateEditor.destroy();
        }
    }
};
</script>

<style scoped>
/* Templates button styling to match v-btn-toggle */
.templates-btn {
    background-color: #FAFAFA !important;
    border-color: rgba(0, 0, 0, 0.12) !important;
    box-shadow: none !important;
    color: rgba(0, 0, 0, 0.87) !important;
    height: 36px;
    min-width: 120px;
}

.templates-btn:hover {
    background-color: #F5F5F5 !important;
    border-color: rgba(0, 0, 0, 0.26) !important;
}

.v-list-item {
    height: 40px; /* Slightly smaller than default for a tighter list */
}

/* Templates dropdown styles */
.template-item {
    transition: background-color 0.2s ease;
}

.template-item:hover {
    background-color: rgba(0, 0, 0, 0.05);
}

.new-template-item {
    background-color: rgba(0, 0, 0, 0.02);
}

.action-icon {
    transition: all 0.2s ease;
    opacity: 0.7;
    cursor: pointer;
}

.action-icon:hover {
    opacity: 1;
    transform: scale(1.1);
}

.v-icon[disabled] {
    opacity: 0.3;
    pointer-events: none;
}

.templates-loading {
    display: flex;
    align-items: center;
    justify-content: center;
}

/* Editor styles */
::v-deep .ProseMirror {
    outline: none;
    min-height: 250px;
    font-size: 16px;
    font-weight: 400;
    color: black;
    border: 1px solid rgba(0, 0, 0, 0.30);
    border-radius: 4px;
    padding: 8px;
    max-height: 60vh;
    overflow-y: auto; /* Enable vertical scrolling when content exceeds the max height */
}

::v-deep .ProseMirror:hover {
    border: 1px solid rgba(0, 0, 0, 0.60);
    transition: .3s ease;
}

::v-deep .ProseMirror:focus {
    outline: 1.3px solid #1976D2;
    border: 1px solid #1976D2;
}

::v-deep .ProseMirror p {
    margin-top: 0;
    /* Reduces space above the paragraph */
    margin-bottom: 0.1em;
    /* Reduces space below the paragraph, adjust as needed */
}

/* Task List Styles */
::v-deep ul[data-type="taskList"] {
    list-style: none;
    padding: 0;
    margin-left: 0;
}

::v-deep ul[data-type="taskList"] li {
    display: flex;
    align-items: flex-start;
    margin-bottom: 0.5em;
}

::v-deep ul[data-type="taskList"] li > label {
    flex: 0 0 auto;
    margin-right: 0.5em;
    user-select: none;
}

::v-deep ul[data-type="taskList"] li > div {
    flex: 1 1 auto;
}

::v-deep ul[data-type="taskList"] input[type="checkbox"] {
    cursor: pointer;
}

/* Support for nested task lists */
::v-deep ul[data-type="taskList"] ul[data-type="taskList"] {
    margin: 0.5em 0 0.5em 1.5em;
}

/* Table styles */
::v-deep .ProseMirror table {
    border-collapse: collapse;
    margin: 0;
    overflow: hidden;
    table-layout: fixed;
    width: 100%;
    margin: 1em 0;
}

::v-deep .ProseMirror table td,
::v-deep .ProseMirror table th {
    border: 1px solid rgba(0, 0, 0, 0.2);
    box-sizing: border-box;
    min-width: 1em;
    padding: 8px;
    position: relative;
    vertical-align: top;
}

::v-deep .ProseMirror table th {
    background-color: #f6f8fa;
    font-weight: bold;
}

::v-deep .ProseMirror table p {
    margin: 0;
}

::v-deep .ProseMirror .selectedCell:after {
    background: rgba(200, 200, 255, 0.4);
    content: "";
    left: 0; right: 0; top: 0; bottom: 0;
    pointer-events: none;
    position: absolute;
    z-index: 2;
}

::v-deep .ProseMirror .column-resize-handle {
    background-color: #1976D2;
    bottom: 0;
    pointer-events: none;
    position: absolute;
    right: -2px;
    top: 0;
    width: 4px;
}

::v-deep .tableWrapper {
    overflow-x: auto;
    padding: 1em 0;
}

::v-deep .resize-cursor {
    cursor: ew-resize;
    cursor: col-resize;
}
</style>